import { Injectable } from '@angular/core';
import { EChartsOption } from 'echarts';
import { ClimateChart } from '../../../model/climate/climate-chart';
import { ClimateChartHelper } from './util/climate-chart-helper';
import { getSSPColor } from './custom-theme';

@Injectable()
export class ConsolidatedRiskScoreChartService {

    formatScoreValue(value: any): string {
        if (value === null || value === undefined || value === 'N/A' || value === '-') {
            return '-';
        }
        const num = Number(value);
        if (isNaN(num)) {
            return String(value);
        }
        return Number.isInteger(num) ? num.toString() : num.toFixed(0);
    }

    buildChartOptions(climateChart: ClimateChart): EChartsOption {
        const chartConfig = climateChart.chartConfig;
        const yAxis = chartConfig.yAxis;

        const source: any[] = [climateChart.xAxis];
        const series: any[] = [];

        for (const [key, value] of Object.entries(climateChart.series)) {
            // Enable dual xAxis for Historical bar centering (same as TXx Score, SPI6 Score, etc.)
            series.push(ClimateChartHelper.getSeriesConfig(key, chartConfig.type, getSSPColor, true));
            const row = ClimateChartHelper.padSSPScenarioData(key, value, climateChart.xAxis).map((cell: any, index: number) => {
                if (index === 0) {
                    return cell;
                }
                if (cell === 'N/A' || cell === null || cell === undefined || cell === '-') {
                    return '-';
                }
                const num = Number(cell);
                return isNaN(num) ? cell : num;
            });
            source.push(row);
        }

        // Taken from the config rather than hardcoded, so the axis the backend declares is the
        // axis that gets drawn. The fallbacks are the FR-1.52 scale, in case a chart arrives
        // without one.
        const yAxisName = yAxis?.title || 'Score';
        const yAxisMin = yAxis?.min ?? 0;
        const yAxisMax = yAxis?.max ?? 5;
        const yAxisInterval = yAxis?.interval ?? 1;

        return {
            height: '80%',
            animation: false,
            animationDuration: 0,
            animationDurationUpdate: 0,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985'
                    }
                },
                valueFormatter: (value: any) => this.formatScoreValue(value)
            },
            legend: { top: 10 },
            toolbox: this.getToolboxConfig(yAxisName),
            dataZoom: {
                show: false,
                start: 0,
                end: 100
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: ClimateChartHelper.getDualXAxisConfig({
                type: 'category',
                boundaryGap: ['10%', '10%'],
                axisLabel: { interval: 0 }
            }),
            yAxis: [
                {
                    type: 'value',
                    scale: false,
                    name: yAxisName,
                    min: yAxisMin,
                    max: yAxisMax,
                    interval: yAxisInterval,
                    position: 'left',
                    alignTicks: true,
                    axisLine: {
                        show: true,
                    },
                    axisLabel: {
                        formatter: (value: any) => this.formatScoreValue(value)
                    },
                    boundaryGap: [0.2, 0.2]
                }
            ],
            dataset: {
                source
            },
            series
        } as EChartsOption;
    }

    toFileName(title: string): string {
        return (title || 'Consolidated_Risk_Score').replace(/[^a-zA-Z0-9]/g, '_');
    }

    private getToolboxConfig(chartTitle: string): any {
        const base = ClimateChartHelper.getToolboxConfig(chartTitle, this.toFileName(chartTitle));
        const dataViewOptionToContent = (opt: any) => {
            let source: any[] | null = null;
            const dataset = opt.dataset;
            if (dataset) {
                if (Array.isArray(dataset) && dataset[0] && dataset[0].source) {
                    source = dataset[0].source;
                } else if (dataset.source) {
                    source = dataset.source;
                }
            }
            if (!source || source.length === 0) {
                return '<div style="padding: 10px;">No data available</div>';
            }

            const headerRow = source[0];
            const rows = source.slice(1);

            let html = '<div style="max-height:300px;overflow:auto;padding:8px;">';
            html += '<table style="border-collapse:collapse;width:100%;font-size:12px;">';
            html += '<thead><tr>';
            html += '<th style="border:1px solid #ddd;padding:4px 8px;text-align:left;">Period</th>';
            for (let i = 1; i < headerRow.length; i++) {
                html += `<th style="border:1px solid #ddd;padding:4px 8px;text-align:center;">${headerRow[i]}</th>`;
            }
            html += '</tr></thead><tbody>';

            rows.forEach((row: any[]) => {
                html += '<tr>';
                html += `<td style="border:1px solid #ddd;padding:4px 8px;white-space:nowrap;">${row[0]}</td>`;
                for (let i = 1; i < headerRow.length; i++) {
                    if (i < row.length && row[i] !== undefined && row[i] !== null && row[i] !== 'N/A') {
                        html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:center;">${this.formatScoreValue(row[i])}</td>`;
                    } else {
                        html += `<td style="border:1px solid #ddd;padding:4px 8px;text-align:center;color:#999;">-</td>`;
                    }
                }
                html += '</tr>';
            });

            html += '</tbody></table></div>';
            return html;
        };

        if (base && base.feature && base.feature.dataView) {
            base.feature.dataView.optionToContent = dataViewOptionToContent;
        }

        return base;
    }
}
